<template>
    <modal-side-generic
	sizeClass="big"
	:title="t('cards.charge.flex.reuseModal.title')"
	:triggerClose="triggerClose"
	:tabs="reuseModalTabsList"
	@tabClicked="selectedTab = $event"
	@hideMe="[emit('hideMe'), triggerClose = false]">
		<!-- Loader -->
		<loader-generic
		v-if="showLoader || showOverlaySpinner"
		:fullSize="!showOverlaySpinner"
		:isOverlay="showOverlaySpinner"/>
		<!-- Error State -->
		<empty-state-error v-if="applyErrorState && !showLoader" :message="t('genericErrorMessage')"/>
        <!-- Tables -->
		<transition name="common-enter-animation" appear>
			<!-- Latest -->
			<table-generic
			v-if="!showLoader && !applyErrorState && latestChargesList.length && selectedTab === 'latest'"
			class="transition-animation"
			:headerData="latestChargesHeaders"
			:tableData="latestChargesList"
			:tableStyles="latestChargesStyles"
			@sortBy="chargesSortByHandler"
			@rowClicked="rowClickHandler($event)"/>
        </transition>
        <transition name="common-enter-animation" appear>
			<!-- Favorites -->
			<table-generic
			v-if="!showLoader && !applyErrorState && favoritesTableData.length > 0 && selectedTab === 'favorites'"
			class="transition-animation"
			:headerData="favoritesHeaderData"
			:tableData="favoritesTableData"
			:tableStyles="favoritesTableStyles"
			:hasMore="getFavoritesHasMore"
			:isLoadingMore="tableLoadingMore"
			:loadingMoreText="t('cards.charge.flex.reuseModal.loadingMoreText')"
			@loadMore="loadMoreHandler"
			@rowClicked="rowClickHandler($event)"/>
		</transition>
        <!-- Empty State Latest -->
		<empty-state-generic
		v-if="!showLoader && !applyErrorState && !latestChargesList.length && selectedTab === 'latest'"
		illustration="emptyState/no-movements"
		:title="t('cards.charge.flex.reuseModal.emptyState.lastTransfers.title')"
		:description="t('cards.charge.flex.reuseModal.emptyState.lastTransfers.description')"/>
		<!-- Empty State Favorites -->
		<empty-state-generic
		v-if="!showLoader && !applyErrorState && !favoritesTableData.length && selectedTab === 'favorites'"
		illustration="emptyState/favorites-folder"
		:title="t('cards.charge.flex.reuseModal.emptyState.favorites.title')"
		:description="t('cards.charge.flex.reuseModal.emptyState.favorites.description')"/>
	</modal-side-generic>
</template>

<script setup>
import ModalSideGeneric from '@/components/generic/modals/side/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import LoaderGeneric from '@/components/generic/loaders/Generic'
import EmptyStateError from '@/components/generic/emptyStates/Error'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'

/* Vue */
import { ref, computed } from 'vue'

/* Aux */
import { sortTableData } from '@/assets/scripts/parsers/tables'
import { ACCOUNT_MAIN_CURRENCY } from '@/assets/constants/accounts'
import { ACCOUNT_NUMBER } from '@/assets/constants/masks'
import { getDate } from '@/assets/scripts/dateBuilders'
import { accountFormatHelper } from '@/assets/scripts/strings'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/** Props
@getLastCharges {Array}
@getCardsList {Array}
@isMobile {Boolean}
*/

const props = defineProps({
	getCards: Array,
    accountsList: Array,
	isMobile: Boolean
})

/* Emmitters */
const emit = defineEmits(['hideMe', 'reuseChargeRowClicked'])

/* Show Data */
const showLoader = ref(false)
const showOverlaySpinner = ref(false)
const triggerClose = ref(false)
const applyErrorState = ref(false)

/* mapActions & mapGetters */
const { requestLastChargesList, requestFavoritesList } = mapActions(moduleMapper.CARDS_CHARGE)
const { getLastChargesList, getFavoritesList, getFavoritesHasMore } = mapGetters(moduleMapper.CARDS_CHARGE)

/*************************************************
*                Reuse Modal Tabs                *
*************************************************/
const selectedTab = ref('latest')

const reuseModalTabsList = computed(() => {
	const tabs = [
		{ id: 'latest', label: t('cards.charge.flex.reuseModal.tabsList.latest') },
		{ id: 'favorites', label: t('cards.charge.flex.reuseModal.tabsList.favorites') }
	]

	return tabs.map(tab => {
		return { ...tab, selected: selectedTab.value === tab.id }
	})
})
/*************************************************
*                Last Charges Data               *
*************************************************/
const latestChargesList = ref(null)

const latestChargesStyles = ref([
	{ width: '150px', 'min-width': '150px' },
	{ 'text-align': 'end' },
	{ width: '130px', 'min-width': '130px' },
	null
])

const latestChargesHeaders = ref([
	{ label: t('cards.charge.flex.reuseModal.tableHeaderData.destinationCard'), dataAttr: 'destinationCard', showInMobile: { groupWith: 'amount' }, rowCellType: 'title', sortable: true },
	{ label: t('cards.charge.flex.reuseModal.tableHeaderData.amount'), dataAttr: 'amount', rowCellType: 'amount', sortable: true },
	{ label: t('cards.charge.flex.reuseModal.tableHeaderData.originAccount'), dataAttr: 'originAccount', sortable: true },
	{ label: t('cards.charge.flex.reuseModal.tableHeaderData.date'), dataAttr: 'date', initialSortDirection: 'Desc', sortable: true }
])

const parsedlatestTransfersList = () => {
	latestChargesList.value = getLastChargesList.value.map(charge => ([
		{ type: 'title', value: charge.codedCardNumber, dataAttr: 'destinationCard', content: charge.cardNumber },
		{ type: 'amount', value: { content: charge.amount, currency: ACCOUNT_MAIN_CURRENCY, noBalanceIcon: true }, dataAttr: 'amount' },
		{ value: accountFormatHelper(charge.originAccountNumber), dataAttr: 'originAccount', content: charge.originAccount },
		{ value: { content: getDate(charge.topupDate), classes: 'capitalize', rawDate: charge.topupDate }, dataAttr: 'date' }
	]))
}

const chargesSortByHandler = (event) => {
	latestChargesList.value = sortTableData(latestChargesList.value, event)
}
/*************************************************
*                 Favorites Data                 *
*************************************************/
const favoritesList = ref(null)

const favoritesTableStyles = ref([
	null,
	{ width: '150px', 'min-width': '150px' },
	{ 'text-align': 'end' },
	{ width: '130px', 'min-width': '130px' }
])

const favoritesHeaderData = ref([
	{ label: t('cards.charge.flex.reuseModal.tableHeaderData.name'), dataAttr: 'name', showInMobile: { groupWith: 'amount' }, rowCellType: 'title' },
	{ label: t('cards.charge.flex.reuseModal.tableHeaderData.destinationCard'), dataAttr: 'destinationCard' },
	{ label: t('cards.charge.flex.reuseModal.tableHeaderData.amount'), dataAttr: 'amount', rowCellType: 'amount' },
	{ label: t('cards.charge.flex.reuseModal.tableHeaderData.originAccount'), dataAttr: 'originAccount' }
])

const favoritesTableData = computed(() => {
	if (!favoritesList.value) return []

	return favoritesList.value.map(favorite => {
		const params = favorite.customerOperationView.parameters
		const amount = getParameterValue(params, 'amount')
		const originAccount = getParameterValue(params, 'originAccount')

		return [
			{ value: favorite.id, dataAttr: 'id' },
			{ type: 'title', value: favorite.alias && favorite.alias.length ? favorite.alias : '-', dataAttr: 'name' },
			{ value: getParameterValue(params, 'cardNumber'), dataAttr: 'destinationCard' },
			{ type: 'amount', value: { content: parseFloat(amount), currency: ACCOUNT_MAIN_CURRENCY, noBalanceIcon: true }, dataAttr: 'amount' },
			{ value: { content: getOriginAccountNumber(favorite.id, 'originAccount'), ...(parseInt(originAccount) && { mask: ACCOUNT_NUMBER }) }, dataAttr: 'originAccount' }
		]
	})
})

const getParameterValue = (paramsList, paramName) => {
	const targetParam = paramsList.find(param => param.name === paramName)
	return targetParam && targetParam.value ? targetParam.value : '-'
}

const getOriginAccountNumber = rowId => {
	const originAccount = getFavoritesList.value.find(favorite => favorite.id === rowId).customerOperationView
	? parseFloat(getFavoritesList.value.find(favorite => favorite.id === rowId).customerOperationView.parameters.find(param => param.name === 'originAccount').value)
	: null

	return props.accountsList.find(account => account.id === originAccount)
	? props.accountsList.find(account => account.id === originAccount).accountNumber
	: '-'
}

const rowClickHandler = event => {
	let selectedRowData = {}

	if (selectedTab.value === 'latest') {
		selectedRowData = {
			accountId: event.rowData.find(params => params.dataAttr === 'originAccount').content,
			cardId: event.rowData.find(params => params.dataAttr === 'destinationCard').content,
			amount: event.rowData.find(params => params.dataAttr === 'amount').value.content,
			currency: ACCOUNT_MAIN_CURRENCY
		}
	} else {
		const favoritesData = getFavoritesList.value.find(favorite => favorite.id === event.rowId).customerOperationView.parameters
		selectedRowData = {
			accountId: parseInt(favoritesData.find(field => field.name === 'originAccount').value),
			cardId: favoritesData.find(field => field.name === 'cardNumber').value,
			amount: parseFloat(favoritesData.find(field => field.name === 'amount').value),
			currenty: ACCOUNT_MAIN_CURRENCY
		}
	}

	emit('reuseChargeRowClicked', selectedRowData)
	emit('hideMe')
}
/*************************************************
*                                                *
*               Load More Favorites              *
*                                                *
*************************************************/
const tableLoadingMore = ref(false)
const favoritesPayload = computed(() => {
    return {
		operationTypes: 500016,
        pageSize: 10,
        pageNumber: 1
    }
})

const loadMoreFavorites = async () => {
    tableLoadingMore.value = true

    try {
        await requestFavoritesList(favoritesPayload.value)
        favoritesList.value = getFavoritesList.value
        tableLoadingMore.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, loadMoreFavorites)
    }
}

const loadMoreHandler = () => {
    favoritesPayload.value.pageNumber++
    loadMoreFavorites()
}
/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const requestReuseTransferModalData = async () => {
	applyErrorState.value = false
	showLoader.value = true

	try {
		await requestLastChargesList({ pageNumber: 0, pageSize: 10 })
		await requestFavoritesList(favoritesPayload.value)
		favoritesList.value = getFavoritesList.value
        parsedlatestTransfersList()
		showLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, requestReuseTransferModalData)
	}
}
/*************************************************
*                                                *
*                 Error Handler                  *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showLoader.value = false
    showOverlaySpinner.value = false
    applyErrorState.value = true

    feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

requestReuseTransferModalData()
</script>

<style lang="scss" scoped>
.reuse-transfer-modal-empty-state {
	box-shadow: none;
}
</style>
